/**
 * ChannelSwitcher — B-2026-0091
 *
 * A nav widget that lists all of the current user's channel memberships
 * (from GET /auth/me/channel-memberships), showing each channel and the
 * user's role in it.  Clicking a channel navigates to that channel's page.
 *
 * Renders as a pill button in the TopBar that opens a dropdown.
 * Falls back gracefully when the endpoint is unavailable (e.g. not yet deployed).
 */

import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { TvIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { useChannelMemberships } from '@/hooks/useChannelMemberships';
import type { ChannelMembershipSummary } from '@/types/channel';

// ─── Role badge ───────────────────────────────────────────────────────────────

interface RoleBadgeProps {
  role: string;
}

function RoleBadge({ role }: RoleBadgeProps) {
  const normalized = role.toLowerCase().replace('channel_', '');
  const label =
    normalized === 'owner'
      ? 'Owner'
      : normalized === 'admin' || normalized === 'coach'
        ? 'Coach'
        : normalized === 'athlete'
          ? 'Athlete'
          : normalized.charAt(0).toUpperCase() + normalized.slice(1);

  const color =
    normalized === 'owner'
      ? 'var(--color-accent-gold)'
      : normalized === 'admin' || normalized === 'coach'
        ? 'var(--color-accent-blue)'
        : 'var(--color-accent)';
  const bg =
    normalized === 'owner'
      ? 'rgba(244,201,93,0.12)'
      : normalized === 'admin' || normalized === 'coach'
        ? 'rgba(61,169,252,0.12)'
        : 'rgba(30,215,96,0.12)';
  const border =
    normalized === 'owner'
      ? 'rgba(244,201,93,0.30)'
      : normalized === 'admin' || normalized === 'coach'
        ? 'rgba(61,169,252,0.30)'
        : 'rgba(30,215,96,0.30)';

  return (
    <span
      style={{
        fontSize: '10px',
        fontWeight: 600,
        padding: '2px 7px',
        borderRadius: '999px',
        backgroundColor: bg,
        color,
        border: `1px solid ${border}`,
        letterSpacing: '0.1px',
        flexShrink: 0,
      }}
    >
      {label}
    </span>
  );
}

// ─── ChannelSwitcher ──────────────────────────────────────────────────────────

interface ChannelSwitcherProps {
  /** Defaults to a compact button; set to true for the sidebar variant */
  compact?: boolean;
}

export default function ChannelSwitcher({ compact = false }: ChannelSwitcherProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: memberships, isLoading } = useChannelMemberships();

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  // Close on navigation
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  function handleSelect(membership: ChannelMembershipSummary) {
    setOpen(false);
    navigate(`/channels/${membership.channel_slug}`);
  }

  // Don't render if no memberships and not loading
  if (!isLoading && (!memberships || memberships.length === 0)) return null;

  const count = memberships?.length ?? 0;

  return (
    <>
      <style>{`
        @keyframes cs-dropdown-in {
          from { opacity: 0; transform: scale(0.95) translateY(-6px); }
          to   { opacity: 1; transform: scale(1)   translateY(0); }
        }
        .cs-dropdown {
          animation: cs-dropdown-in 0.17s cubic-bezier(0.22, 1, 0.36, 1) both;
          transform-origin: top ${compact ? 'left' : 'right'};
        }
        .cs-row {
          display: flex;
          align-items: center;
          gap: 10px;
          width: 100%;
          padding: 9px 12px;
          border-radius: 8px;
          border: none;
          background: transparent;
          cursor: var(--cursor-hand);
          text-align: left;
          transition: background-color 130ms ease;
        }
        .cs-row:hover { background-color: var(--color-dark-elevated-2); }
        .cs-row.cs-active { background-color: rgba(30,215,96,0.07); }
      `}</style>

      <div ref={containerRef} style={{ position: 'relative' }}>
        {/* Trigger button */}
        <button
          type="button"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label="Switch channel"
          onClick={() => setOpen((prev) => !prev)}
          data-testid="channel-switcher-trigger"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '5px 10px',
            borderRadius: '999px',
            border: '1px solid var(--color-border-subtle)',
            backgroundColor: open
              ? 'var(--color-dark-elevated-2)'
              : 'var(--color-dark-elevated-1)',
            color: 'var(--color-text-secondary)',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: 500,
            transition: 'background-color 120ms ease, border-color 120ms ease',
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.backgroundColor =
              'var(--color-dark-elevated-2)';
            (e.currentTarget as HTMLButtonElement).style.borderColor =
              'var(--color-border-strong)';
          }}
          onMouseLeave={(e) => {
            if (!open) {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                'var(--color-dark-elevated-1)';
            }
            (e.currentTarget as HTMLButtonElement).style.borderColor =
              'var(--color-border-subtle)';
          }}
        >
          <TvIcon style={{ width: '16px', height: '16px', flexShrink: 0 }} />
          {!compact && (
            <span style={{ whiteSpace: 'nowrap' }}>
              {isLoading ? 'Channels' : count === 1 ? memberships![0].channel_name : `${count} Channels`}
            </span>
          )}
          {isLoading ? (
            <span
              style={{
                width: '12px',
                height: '12px',
                border: '1.5px solid var(--color-border-strong)',
                borderTopColor: 'var(--color-accent)',
                borderRadius: '50%',
                display: 'inline-block',
                animation: 'spin 0.7s linear infinite',
                flexShrink: 0,
              }}
            />
          ) : (
            <ChevronDownIcon
              style={{
                width: '13px',
                height: '13px',
                flexShrink: 0,
                transition: 'transform 150ms ease',
                transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            />
          )}
        </button>

        {/* Dropdown */}
        {open && memberships && memberships.length > 0 && (
          <div
            role="menu"
            aria-label="Your channels"
            className="cs-dropdown"
            style={{
              position: 'absolute',
              [compact ? 'left' : 'right']: 0,
              top: 'calc(100% + 8px)',
              zIndex: 50,
              minWidth: '260px',
              maxWidth: '320px',
              borderRadius: '14px',
              border: '1px solid var(--color-border-subtle)',
              backgroundColor: 'var(--color-dark-elevated-1)',
              padding: '6px',
              boxShadow: 'var(--shadow-modal)',
            }}
          >
            <p
              style={{
                fontSize: '11px',
                fontWeight: 600,
                color: 'var(--color-text-muted)',
                letterSpacing: '0.07em',
                textTransform: 'uppercase',
                padding: '6px 12px 4px',
                margin: 0,
              }}
            >
              Your channels
            </p>

            {memberships.map((m) => {
              const isActive =
                pathname === `/channels/${m.channel_slug}` ||
                pathname.startsWith(`/channels/${m.channel_slug}/`);
              return (
                <button
                  key={m.channel_slug}
                  role="menuitem"
                  className={`cs-row${isActive ? ' cs-active' : ''}`}
                  onClick={() => handleSelect(m)}
                  data-testid={`channel-option-${m.channel_slug}`}
                >
                  {/* Channel avatar (initials) */}
                  <div
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '8px',
                      backgroundColor: isActive
                        ? 'rgba(30,215,96,0.15)'
                        : 'var(--color-dark-elevated-2)',
                      border: `1px solid ${isActive ? 'rgba(30,215,96,0.30)' : 'var(--color-border-subtle)'}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                    aria-hidden="true"
                  >
                    <span
                      style={{
                        fontSize: '11px',
                        fontWeight: 700,
                        color: isActive ? 'var(--color-accent)' : 'var(--color-text-muted)',
                      }}
                    >
                      {m.channel_name.charAt(0).toUpperCase()}
                    </span>
                  </div>

                  {/* Name */}
                  <span
                    style={{
                      flex: 1,
                      fontSize: '13px',
                      fontWeight: 500,
                      color: isActive ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {m.channel_name}
                  </span>

                  {/* Role badge */}
                  <RoleBadge role={m.member_role} />
                </button>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
